import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  StorefrontOutlined as StorefrontIcon,
  CompareArrows as CompareArrowsIcon,
  FileDownloadOutlined as FileDownloadIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitleIcon from "../components/PageTitleIcon";
import BranchAnalysisCard from "../components/dashboard/BranchAnalysisCard";
import BranchComparisonDialog from "../components/dashboard/BranchComparisonDialog";
import type { BranchCompareTourPhase } from "../components/dashboard/BranchComparisonDialog";
import StatCard from "../components/dashboard/StatCard";
import { SetupCoachmark } from "../components/selfSetup/SetupCoachmark";
import { BRANCH_ANALYSIS_RADIUS } from "../constants/branchAnalysis.styles";
import {
  HELP_GUIDE_BRANCH_ANALYSIS,
  HELP_GUIDE_COMPARE_BRANCHES,
} from "../constants/knowledgeBaseTryIt";
import { useSelfSetupTour } from "../hooks/useSelfSetupTour";
import { helpI18n } from "../i18n/help";
import { useDashboardStore } from "../stores/dashboard.store";
import { useOrganizationStore } from "../stores/organization.store";
import { OrganizationType } from "../types/organization.type";
import type { BranchOperationalRow } from "../types/dashboard.type";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import {
  ALL_TIME_MONTH_VALUE,
  buildDashboardMonthOptions,
  getCurrentDashboardMonthValue,
  resolveDashboardMonthValue,
} from "../utils/dashboardMonth.helpers";
import { resolveBranchSiteAccess } from "../utils/esgByBranch.helpers";
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { getEsgBranchWasteExport } from "../services/esg-dashboard.service";
import {
  buildBranchWasteExportFilename,
  buildBranchWasteExportWorkbook,
  downloadXlsxBuffer,
} from "../utils/branchWasteExportXlsx.helpers";

const UNASSIGNED_SITE_ID = "__unassigned__";

type HelpOverviewPhase = "month" | "stats" | "branches";

function safeNum(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

function calculateTrend(current: number, previous: number | undefined): number {
  if (previous === undefined || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

function filterBranchesBySiteAccess(
  branches: BranchOperationalRow[],
  unassigned: BranchOperationalRow | null,
  sitesAccess: string[] | "all" | "none",
): BranchOperationalRow[] {
  const access = resolveBranchSiteAccess(sitesAccess);

  if (access === "none") {
    return [];
  }

  const rows = [...branches];
  if (unassigned && access === "all") {
    rows.push(unassigned);
  }

  if (access === "all") {
    return rows;
  }

  const allowed = new Set(access);
  const filtered = branches.filter((row) => allowed.has(row.collection_site_id));
  if (unassigned && allowed.has(UNASSIGNED_SITE_ID)) {
    filtered.push(unassigned);
  }
  return filtered;
}

const BranchAnalysis: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation("translation", { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useOrganizationStore();
  const { sitesForOrg } = useAuthPermissions();
  const {
    byBranch,
    isLoadingByBranch,
    byBranchError,
    selectedMonth,
    fetchDashboardByBranch,
    setSelectedMonth,
    loadDashboardPreferences,
  } = useDashboardStore();
  const { isStep, tourPhase, setTourPhase, completeStep, dismissTour } =
    useSelfSetupTour();
  const showBranchTour = isStep("branchAnalysis");

  const [helpOverview, setHelpOverview] = useState(false);
  const [helpOverviewPhase, setHelpOverviewPhase] =
    useState<HelpOverviewPhase>("month");
  const [helpCompare, setHelpCompare] = useState(false);

  const showMonthTip =
    (showBranchTour && tourPhase === "monthSelect") ||
    (helpOverview && helpOverviewPhase === "month");
  const showStatsTip =
    (showBranchTour && tourPhase === "keyNumbers") ||
    (helpOverview && helpOverviewPhase === "stats");
  const showBranchesTip =
    (showBranchTour && tourPhase === "branches") ||
    (helpOverview && helpOverviewPhase === "branches");
  const showCompareTip = showBranchTour && tourPhase === "compare";
  const selfSetupCompareDialogPhase: BranchCompareTourPhase | null =
    tourPhase === "compareSelect"
      ? "select"
      : tourPhase === "compareResults"
        ? "results"
        : tourPhase === "compareExport"
          ? "export"
          : null;
  const showCompareDialogTour =
    (showBranchTour && selfSetupCompareDialogPhase != null) || helpCompare;

  const [isExportingXlsx, setIsExportingXlsx] = useState(false);
  const [exportXlsxError, setExportXlsxError] = useState<string | null>(null);

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareDialogPhase, setCompareDialogPhase] =
    useState<BranchCompareTourPhase>("select");

  const activeCompareDialogPhase: BranchCompareTourPhase =
    selfSetupCompareDialogPhase ?? compareDialogPhase;

  const availableMonths = useMemo(
    () => [
      ...buildDashboardMonthOptions(),
      { value: ALL_TIME_MONTH_VALUE, label: t("branchAnalysis.allTime") },
    ],
    [t],
  );
  const monthValue = resolveDashboardMonthValue(selectedMonth, availableMonths);
  const isAllTime = monthValue === ALL_TIME_MONTH_VALUE;

  const isWasteOutlet =
    selectedOrganization?.organization_type === OrganizationType.WASTE_OUTLET;

  useEffect(() => {
    loadDashboardPreferences();
  }, [loadDashboardPreferences]);

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get("helpGuide");
    if (
      guide !== HELP_GUIDE_BRANCH_ANALYSIS &&
      guide !== HELP_GUIDE_COMPARE_BRANCHES
    ) {
      return;
    }
    markHelpGuideActive(guide);
    if (guide === HELP_GUIDE_BRANCH_ANALYSIS) {
      setHelpOverview(true);
      setHelpOverviewPhase("month");
    } else {
      setHelpCompare(true);
      setCompareOpen(true);
      setCompareDialogPhase("select");
    }
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!selectedOrganization?.id || !isWasteOutlet) return;

    const resolved = resolveDashboardMonthValue(selectedMonth, availableMonths);
    if (resolved !== selectedMonth) {
      setSelectedMonth(resolved);
      return;
    }

    fetchDashboardByBranch(selectedOrganization.id, resolved);
  }, [
    selectedOrganization?.id,
    selectedOrganization?.organization_type,
    selectedMonth,
    isWasteOutlet,
    fetchDashboardByBranch,
    setSelectedMonth,
    availableMonths,
  ]);

  useEffect(() => {
    if (!showMonthTip && !showStatsTip && !showBranchesTip && !showCompareTip) {
      return;
    }
    const id = showMonthTip
      ? "self-setup-branch-month"
      : showStatsTip
        ? "self-setup-branch-stats"
        : showBranchesTip
          ? "self-setup-branch-cards"
          : "self-setup-branch-compare";
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showMonthTip, showStatsTip, showBranchesTip, showCompareTip]);

  // Keep compare dialog open while its self-setup tips are active (incl. resume).
  useEffect(() => {
    if (!showBranchTour) return;
    if (
      tourPhase === "compareSelect" ||
      tourPhase === "compareResults" ||
      tourPhase === "compareExport"
    ) {
      setCompareOpen(true);
    }
  }, [showBranchTour, tourPhase]);

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
  };

  const handleExportXlsx = async () => {
    if (!selectedOrganization?.id) return;
    setIsExportingXlsx(true);
    setExportXlsxError(null);
    try {
      const exportData = await getEsgBranchWasteExport(
        "waste-outlet",
        selectedOrganization.id,
      );
      const buffer = await buildBranchWasteExportWorkbook(exportData);
      downloadXlsxBuffer(
        buffer,
        buildBranchWasteExportFilename(selectedOrganization.name ?? ""),
      );
    } catch (error) {
      setExportXlsxError(
        error instanceof Error ? error.message : t("branchAnalysis.export.error"),
      );
    } finally {
      setIsExportingXlsx(false);
    }
  };

  const openCompareDialog = () => {
    setCompareOpen(true);
    if (showBranchTour && tourPhase === "compare") {
      setTourPhase("compareSelect");
    }
    if (helpCompare) {
      setCompareDialogPhase("select");
    }
  };

  const closeCompareDialog = () => {
    setCompareOpen(false);
    if (
      showBranchTour &&
      (tourPhase === "compareSelect" ||
        tourPhase === "compareResults" ||
        tourPhase === "compareExport")
    ) {
      setTourPhase("compare");
    }
    if (helpCompare) {
      setHelpCompare(false);
      clearHelpGuideActive();
      setCompareDialogPhase("select");
    }
  };

  const finishHelpOverview = () => {
    setHelpOverview(false);
    setHelpOverviewPhase("month");
    clearHelpGuideActive();
  };

  const finishHelpCompare = () => {
    setCompareOpen(false);
    setHelpCompare(false);
    setCompareDialogPhase("select");
    clearHelpGuideActive();
  };

  const advanceBranchTip = () => {
    if (helpOverview) {
      if (helpOverviewPhase === "month") {
        setHelpOverviewPhase("stats");
        return;
      }
      if (helpOverviewPhase === "stats") {
        setHelpOverviewPhase("branches");
        return;
      }
      finishHelpOverview();
      return;
    }
    if (tourPhase === "monthSelect") {
      setTourPhase("keyNumbers");
      return;
    }
    if (tourPhase === "keyNumbers") {
      setTourPhase("branches");
      return;
    }
    if (tourPhase === "branches") {
      setTourPhase("compare");
      return;
    }
    if (tourPhase === "compare") {
      openCompareDialog();
      return;
    }
    completeStep("branchAnalysis");
  };

  const advanceCompareDialogTour = () => {
    if (showBranchTour && selfSetupCompareDialogPhase) {
      if (tourPhase === "compareSelect") {
        setTourPhase("compareResults");
        return;
      }
      if (tourPhase === "compareResults") {
        setTourPhase("compareExport");
        return;
      }
      setCompareOpen(false);
      completeStep("branchAnalysis");
      return;
    }
    if (compareDialogPhase === "select") {
      setCompareDialogPhase("results");
      return;
    }
    if (compareDialogPhase === "results") {
      setCompareDialogPhase("export");
      return;
    }
    if (helpCompare) {
      finishHelpCompare();
      return;
    }
    setCompareOpen(false);
    completeStep("branchAnalysis");
  };

  const finishBranchTip = () => {
    if (helpOverview) {
      finishHelpOverview();
      return;
    }
    if (helpCompare) {
      finishHelpCompare();
      return;
    }
    if (showBranchTour) {
      // Keep current tourPhase so dismissTour saves exact tip (incl. compare*).
      setCompareOpen(false);
      dismissTour();
      return;
    }
    dismissTour();
  };

  const sitesAccess = selectedOrganization?.id
    ? sitesForOrg(selectedOrganization.id)
    : "none";

  const branchRows = useMemo(() => {
    if (!byBranch) return [];
    return filterBranchesBySiteAccess(
      byBranch.branches ?? [],
      byBranch.unassigned_waste,
      sitesAccess,
    );
  }, [byBranch, sitesAccess]);

  const monthTipTitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisMonthTitle")
    : t("selfSetup.tour.branchMonthTitle");
  const monthTipSubtitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisMonthSubtitle")
    : t("selfSetup.tour.branchMonthSubtitle");
  const statsTipTitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisStatsTitle")
    : t("selfSetup.tour.branchStatsTitle");
  const statsTipSubtitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisStatsSubtitle")
    : t("selfSetup.tour.branchStatsSubtitle");
  const cardsTipTitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisCardsTitle")
    : t("selfSetup.tour.branchCardsTitle");
  const cardsTipSubtitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisCardsSubtitle")
    : t("selfSetup.tour.branchCardsSubtitle");
  const compareTourGuide = showCompareDialogTour
    ? {
        active: true as const,
        phase: activeCompareDialogPhase,
        onDismiss: finishBranchTip,
        onNext: advanceCompareDialogTour,
        selectTitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesSelectTitle")
          : t("selfSetup.tour.branchCompareSelectTitle"),
        selectSubtitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesSelectSubtitle")
          : t("selfSetup.tour.branchCompareSelectSubtitle"),
        resultsTitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesResultsTitle")
          : t("selfSetup.tour.branchCompareResultsTitle"),
        resultsSubtitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesResultsSubtitle")
          : t("selfSetup.tour.branchCompareResultsSubtitle"),
        exportTitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesExportTitle")
          : t("selfSetup.tour.branchCompareExportTitle"),
        exportSubtitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesExportSubtitle")
          : t("selfSetup.tour.branchCompareExportSubtitle"),
        nextLabel: t("selfSetup.tourNext"),
        doneLabel: t("selfSetup.tourDone"),
      }
    : null;
  if (!selectedOrganization?.id) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography>{t("branchAnalysis.selectOrganization")}</Typography>
      </Box>
    );
  }

  if (!isWasteOutlet) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography color="text.secondary">
          {t("branchAnalysis.wrongOrgType")}
        </Typography>
      </Box>
    );
  }

  const renderSkeleton = () => (
    <Grid container spacing={2}>
      {[1, 2, 3].map((key) => (
        <Grid item xs={12} sm={6} lg={4} key={key}>
          <Skeleton
            variant="rounded"
            height={320}
            sx={{ borderRadius: BRANCH_ANALYSIS_RADIUS }}
          />
        </Grid>
      ))}
    </Grid>
  );

  const monthSelect = (
    <Stack spacing={0.75} sx={{ minWidth: 200 }} id="self-setup-branch-month">
      <Typography variant="caption" color="text.secondary">
        {t("branchAnalysis.period")}
      </Typography>
      <Select
        size="small"
        value={monthValue}
        onChange={(event) => handleMonthChange(event.target.value)}
        sx={{ minWidth: 200 }}
        displayEmpty
        renderValue={(value) => {
          const label = availableMonths.find((m) => m.value === value)?.label;
          return (
            label ?? availableMonths[0]?.label ?? getCurrentDashboardMonthValue()
          );
        }}
      >
        {availableMonths.map((month) => (
          <MenuItem key={month.value} value={month.value}>
            {month.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );

  const compareButton = (
    <Button
      id="self-setup-branch-compare"
      variant="outlined"
      size="small"
      startIcon={<CompareArrowsIcon />}
      onClick={openCompareDialog}
      disabled={branchRows.length < 2 || isLoadingByBranch}
      sx={{
        borderRadius: BRANCH_ANALYSIS_RADIUS,
        whiteSpace: "nowrap",
        height: 40,
      }}
    >
      {t("branchAnalysis.compare.button")}
    </Button>
  );

  const exportButton = (
    <Button
      id="self-setup-branch-export"
      variant="outlined"
      size="small"
      startIcon={<FileDownloadIcon />}
      onClick={handleExportXlsx}
      disabled={isExportingXlsx}
      sx={{
        borderRadius: BRANCH_ANALYSIS_RADIUS,
        whiteSpace: "nowrap",
        height: 40,
      }}
    >
      {isExportingXlsx
        ? t("branchAnalysis.export.exporting")
        : t("branchAnalysis.export.button")}
    </Button>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: BRANCH_ANALYSIS_RADIUS,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PageTitleIcon icon={<StorefrontIcon />} />
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t("branchAnalysis.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("branchAnalysis.subtitle", { name: selectedOrganization.name })}
            </Typography>
          </Box>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
        >
          <SetupCoachmark
            open={showMonthTip}
            title={monthTipTitle}
            subtitle={monthTipSubtitle}
            onDismiss={finishBranchTip}
            onNext={advanceBranchTip}
            nextLabel={t("selfSetup.tourNext")}
          >
            {monthSelect}
          </SetupCoachmark>
          {!showMonthTip ? (
            <SetupCoachmark
              open={showCompareTip}
              title={t("selfSetup.tour.branchCompareTitle")}
              subtitle={t("selfSetup.tour.branchCompareSubtitle")}
              onDismiss={finishBranchTip}
              onNext={advanceBranchTip}
              nextLabel={t("selfSetup.tourNext")}
            >
              {compareButton}
            </SetupCoachmark>
          ) : (
            compareButton
          )}
          {exportButton}
        </Stack>
      </Paper>

      {exportXlsxError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setExportXlsxError(null)}
        >
          {exportXlsxError}
        </Alert>
      )}

      <BranchComparisonDialog
        open={compareOpen}
        onClose={closeCompareDialog}
        branches={branchRows}
        organizationName={selectedOrganization?.name ?? ""}
        organizationLogoUrl={selectedOrganization?.logo_url ?? null}
        selectedMonth={monthValue}
        tourGuide={compareTourGuide}
      />

      {byBranchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("branchAnalysis.errorLoading", { error: byBranchError })}
        </Alert>
      )}

      {isLoadingByBranch && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {isLoadingByBranch && showStatsTip ? (
        <SetupCoachmark
          open
          title={statsTipTitle}
          subtitle={statsTipSubtitle}
          onDismiss={finishBranchTip}
          onNext={advanceBranchTip}
          nextLabel={t("selfSetup.tourNext")}
          fullWidth
        >
          <Box id="self-setup-branch-stats" sx={{ width: "100%", mb: 3 }}>
            {renderSkeleton()}
          </Box>
        </SetupCoachmark>
      ) : null}

      {byBranch && !isLoadingByBranch && (
        <SetupCoachmark
          open={showStatsTip}
          title={statsTipTitle}
          subtitle={statsTipSubtitle}
          onDismiss={finishBranchTip}
          onNext={advanceBranchTip}
          nextLabel={t("selfSetup.tourNext")}
          fullWidth
        >
          <Box
            id="self-setup-branch-stats"
            sx={{
              display: "grid",
              gap: 2.5,
              mb: 3,
              width: "100%",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              title={t("branchAnalysis.summaryCards.pickupOrders")}
              value={safeNum(byBranch.total_pickup_orders)}
              unit=""
              hideTrend={isAllTime}
              trend={calculateTrend(
                safeNum(byBranch.total_pickup_orders),
                byBranch.previous_month_total_pickup_orders,
              )}
            />
            <StatCard
              title={t("branchAnalysis.summaryCards.totalWaste")}
              value={safeNum(byBranch.total_waste_kg)}
              unit="kg"
              hideTrend={isAllTime}
              trend={calculateTrend(
                safeNum(byBranch.total_waste_kg),
                byBranch.previous_month_total_waste_kg,
              )}
            />
            <StatCard
              title={t("branchAnalysis.summaryCards.completedWaste")}
              value={safeNum(byBranch.completed_waste_kg)}
              unit="kg"
              hideTrend={isAllTime}
              trend={calculateTrend(
                safeNum(byBranch.completed_waste_kg),
                byBranch.previous_month_completed_waste_kg,
              )}
            />
            <StatCard
              title={t("branchAnalysis.summaryCards.revenue")}
              value={safeNum(byBranch.total_revenue)}
              unit="SAR"
              hideTrend={isAllTime}
              trend={calculateTrend(
                safeNum(byBranch.total_revenue),
                byBranch.previous_month_total_revenue,
              )}
            />
            <StatCard
              title={t("branchAnalysis.summaryCards.diversionRate")}
              value={safeNum(byBranch.waste_diversion_rate_percent)}
              unit="%"
              hideTrend={isAllTime}
              trend={calculateTrend(
                safeNum(byBranch.waste_diversion_rate_percent),
                byBranch.previous_month_waste_diversion_rate_percent,
              )}
            />
          </Box>
        </SetupCoachmark>
      )}

      {isLoadingByBranch && !showStatsTip && renderSkeleton()}

      {!isLoadingByBranch &&
        !byBranchError &&
        branchRows.length === 0 &&
        !showBranchesTip && (
        <Typography variant="body2" color="text.secondary">
          {t("branchAnalysis.empty")}
        </Typography>
      )}

      {!isLoadingByBranch && branchRows.length === 0 && showBranchesTip ? (
        <SetupCoachmark
          open
          title={cardsTipTitle}
          subtitle={cardsTipSubtitle}
          onDismiss={finishBranchTip}
          onNext={advanceBranchTip}
          nextLabel={
            helpOverview ? t("selfSetup.tourDone") : t("selfSetup.tourNext")
          }
          fullWidth
        >
          <Box id="self-setup-branch-cards" sx={{ width: "100%", py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("branchAnalysis.empty")}
            </Typography>
          </Box>
        </SetupCoachmark>
      ) : null}

      {!isLoadingByBranch && branchRows.length > 0 && (
        <SetupCoachmark
          open={showBranchesTip}
          title={cardsTipTitle}
          subtitle={cardsTipSubtitle}
          onDismiss={finishBranchTip}
          onNext={advanceBranchTip}
          nextLabel={
            helpOverview ? t("selfSetup.tourDone") : t("selfSetup.tourNext")
          }
          fullWidth
        >
          <Grid container spacing={2} id="self-setup-branch-cards">
            {branchRows.map((row) => (
              <Grid item xs={12} sm={6} lg={4} key={row.collection_site_id}>
                <BranchAnalysisCard
                  branch={row}
                  isUnassigned={row.collection_site_id === UNASSIGNED_SITE_ID}
                />
              </Grid>
            ))}
          </Grid>
        </SetupCoachmark>
      )}
    </Box>
  );
};

export default BranchAnalysis;
